import type { Scope, Session } from '@aurora/contracts';
import { SCOPES, widestScope } from '@aurora/contracts';

/**
 * How much of the desk somebody can see, read from the session.
 *
 * `can('ticket.view')` only says yes or no, and the answer is yes for
 * everyone: a requester sees their own requests, a restricted agent sees what
 * is assigned to them. Screens built on the yes alone offered both of them
 * tools for a queue they cannot see — an "Unassigned" filter that could only
 * ever come back empty, a dashboard announcing that "every open ticket has an
 * owner" to someone shown none of them. The server enforces the scope either
 * way; this is so the interface stops promising what it cannot show.
 */

export type TicketScope = Scope | 'none';

export function ticketViewScope(session: Session | null | undefined): TicketScope {
  if (!session) return 'none';
  const scopes = session.permissions
    .filter((grant) => grant.permission === 'ticket.view')
    .map((grant) => grant.scope)
    .filter((scope): scope is Scope => (SCOPES as readonly string[]).includes(scope));
  return widestScope(scopes) ?? 'none';
}

/** Somebody who raises tickets rather than working them: the list is "My requests". */
export function isRequesterView(session: Session | null | undefined): boolean {
  if (!session) return false;
  return !session.is_agent || ticketViewScope(session) === 'own';
}

/** An agent who sees only tickets assigned to them personally. */
export function seesOnlyAssigned(session: Session | null | undefined): boolean {
  return ticketViewScope(session) === 'assigned';
}
